import * as React from "react"
import { format } from "date-fns"
import { toZonedTime } from "date-fns-tz"

import { useActiveWorkspaceTimezone } from "@/features/auth/hooks/use-active-workspace-timezone"

/**
 * "Now", as the CHURCH's wall clock reads it (IBA-118).
 *
 * Every period preset on a screen begins by asking what day it is — and the browser answers with the day it is
 * *where the user is standing*. A treasurer opening the books from London sees "this month" start on a
 * different instant than the same church's screen in Kigali, and the totals legitimately differ. A church's
 * books do not move when someone travels.
 *
 * The returned Date is a WALL-CLOCK date: its local fields (`getMonth()`, `getDate()`, …) read as the church's
 * time. Feed it to `resolvePeriodRange` and format the result as `YYYY-MM-DD`.
 *
 * NEVER send the result to the API as an instant (`toISOString()`). The API expects a calendar day, because
 * only the backend knows the BRANCH's timezone; an instant is passed through untouched, so a browser-local
 * midnight becomes a real — and wrong — cutoff in the ledger.
 */
export function useChurchNow(): Date {
  const timeZone = useActiveWorkspaceTimezone()
  const zoned = toZonedTime(new Date(), timeZone)

  // Identity is stable within a church day, so a caller memoising a RANGE on `now` doesn't recompute on every
  // render — but it does change when the church's date rolls over, which a memo keyed on the timezone alone
  // would never do (a tab left open overnight would keep reporting yesterday).
  const dayKey = format(zoned, "yyyy-MM-dd")

  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the church DAY, deliberately, not the instant
  return React.useMemo(() => zoned, [dayKey, timeZone])
}
